
import { useState, useEffect } from 'react';
import { SUPPORTED_COUNTRIES } from '../config/paystack';

interface User {
  id: string;
  name: string;
  email: string;
  country: string;
  shares: number;
  balance: number;
  currency: string;
  sharesConfirmed: boolean;
  payoutMethod?: string;
  mobileNumber?: string;
  bankAccountNumber?: string;
  accountHolderName?: string;
  bankName?: string;
  loanApproved?: boolean;
  idNumber?: string;
  fullNamesOnId?: string;
  idCardUploaded?: boolean;
  idVerified?: boolean;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored user data
    const storedUser = localStorage.getItem('brightstone_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        localStorage.removeItem('brightstone_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock user data - default to Kenya if no stored country
      const userData: User = {
        id: '1',
        name: email.split('@')[0].charAt(0).toUpperCase() + email.split('@')[0].slice(1),
        email,
        country: 'KE',
        shares: 0,
        balance: 0,
        currency: 'KES',
        sharesConfirmed: false,
        loanApproved: false,
        idVerified: false,
        idCardUploaded: false
      };
      
      localStorage.setItem('brightstone_user', JSON.stringify(userData));
      setUser(userData);
      
      // Set currency in localStorage based on user's country
      localStorage.setItem('selected_currency', userData.currency);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string, country: string = 'KE') => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Get currency based on selected country
      const countryInfo = SUPPORTED_COUNTRIES.find(c => c.code === country);
      const currency = countryInfo?.currency || 'KES';
      
      const userData: User = {
        id: Date.now().toString(),
        name,
        email,
        country,
        shares: 0,
        balance: 0,
        currency,
        sharesConfirmed: false,
        loanApproved: false,
        idVerified: false,
        idCardUploaded: false
      };
      
      localStorage.setItem('brightstone_user', JSON.stringify(userData));
      setUser(userData);
      
      // Set currency in localStorage based on user's selected country
      localStorage.setItem('selected_currency', currency);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Registration failed' };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('brightstone_user');
    localStorage.removeItem('selected_currency');
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      
      // If country is being updated, also update currency
      if (updates.country) {
        const countryInfo = SUPPORTED_COUNTRIES.find(c => c.code === updates.country);
        if (countryInfo) {
          updatedUser.currency = countryInfo.currency;
          localStorage.setItem('selected_currency', countryInfo.currency);
        }
      }
      
      localStorage.setItem('brightstone_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
  };

  return {
    user,
    loading,
    login,
    register,
    logout,
    updateUser
  };
}
